import Spinner from 'components/Spinner/Spinner'
import SpinnerMessage from 'components/Spinner/SpinnerMessage'
import { Colors } from 'constants/colors'
import { format } from 'date-fns'
import {
  useCreateTargetCreatorsMutation,
  useGetCreatorsQuery,
  useRefreshTargetCreatorsMutation,
} from 'graphql/generated/graphql'
import React from 'react'
import AnalyzeCreatorsInitial from './components/AnalyzeCreatorsInitial'
import CreatorCard from './components/CreatorCard'
import SyncIcon from '@mui/icons-material/Sync'
import { Box, Button, Chip, Modal, TextField, Typography } from '@mui/material'
import IconStyle from 'components/SocialMediaIcon/Style'

export default function AnalyzeCreators(): JSX.Element {
  const [creators, setCreators] = React.useState<string[]>([])
  const [open, setOpen] = React.useState(false)
  const textFieldValue = React.useRef<HTMLInputElement | null>(null)
  const {
    data,
    loading: creatorsLoading,
    refetch,
  } = useGetCreatorsQuery({
    fetchPolicy: 'network-only',
  })
  const [createTargetCreators, { loading }] = useCreateTargetCreatorsMutation({
    variables: {
      creators,
    },
  })

  const [refreshTargetCreators, { loading: refreshLoading }] =
    useRefreshTargetCreatorsMutation()

  React.useEffect(() => {
    setCreators(data?.getCreators.creators ?? [])
  }, [data?.getCreators])

  if (creatorsLoading) {
    return <Spinner />
  }

  if (loading || refreshLoading) {
    return (
      <div style={{ position: 'relative', height: '100%' }}>
        <SpinnerMessage message="Please wait while we gather data from creators for you..." />
      </div>
    )
  }

  if (data?.getCreators.creators.length === 0 && !creatorsLoading) {
    return (
      <AnalyzeCreatorsInitial
        onSubmit={createTargetCreators}
        creators={creators}
        setCreators={setCreators}
        refetch={refetch}
      />
    )
  }

  return (
    <div style={{ color: 'white' }}>
      <h2
        style={{
          margin: '20px 0 0',
          padding: 0,
          color: 'white',
        }}
      >
        Analyze creators
      </h2>
      <p
        style={{
          fontSize: '14px',
          fontWeight: 400,
          color: Colors.darkCoolGray,
          opacity: 0.7,
          margin: '0 0 40px',
          padding: 0,
        }}
      >
        Gain Insights into Your Niche with Comprehensive Creator Analysis
      </p>
      {data?.getCreators.createdAt && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            marginBottom: '10px',
            color: 'rgba(255, 255, 255, 0.7)',
          }}
        >
          <span>{`Last updated: ${format(
            new Date(data.getCreators.createdAt),
            'd MMM y HH:mm'
          )}`}</span>

          <SyncIcon
            sx={{ color: Colors.blue, cursor: 'pointer' }}
            onClick={() => {
              setOpen(true)
            }}
          />
        </div>
      )}
      {data?.getCreators.info.length !== 0 ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '20px',
          }}
        >
          {data?.getCreators.info.map((item, index) => {
            return (
              <CreatorCard
                followersCount={item?.followersCount}
                hashtags={item?.hashtags}
                username={item?.username}
                likesCount={item?.likesCount}
                commentsCount={item?.commentsCount}
                key={item?.id}
                avatarUrl={item?.avatarUrl}
                summary={item?.summary}
                relatedProfiles={item?.relatedProfiles}
              />
            )
          })}
        </div>
      ) : (
        <Spinner />
      )}
      <Modal
        open={open}
        onClose={() => {
          setOpen(false)
          setCreators(data?.getCreators.creators ?? [])
        }}
      >
        <Box
          sx={{
            position: 'absolute' as 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: '800px',
            width: '100%',
            bgcolor: Colors.backgroundDark,
            boxShadow: 24,
            padding: '20px 40px',
            borderRadius: '10px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '15px',
            overflow: 'hidden',
            color: Colors.white,
          }}
        >
          <h4
            style={{ marginBottom: 0, alignSelf: 'flex-start', color: 'white' }}
          >
            Refresh Creator Information:{' '}
          </h4>
          <p style={{ marginTop: '0', color: Colors.darkCoolGray }}>
            You have the option to refresh the content of the previously entered
            creators or input new accounts. Choose whether to update existing
            information or explore new creators.
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0 15vw',
            }}
          >
            <TextField
              id="standard-basic"
              label="Add Creator"
              variant="outlined"
              fullWidth
              inputRef={textFieldValue}
              disabled={creators.length > 2}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  console.log('enter')
                }
              }}
              sx={{
                width: '400px',
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.23)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-disabled': {
                    color: 'rgba(255, 255, 255, 0.3)',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: 'primary.main',
                  },
                  '&.Mui-disabled': {
                    color: 'rgba(255, 255, 255, 0.3)',
                  },
                },
              }}
            />
            <Button
              disabled={creators.length > 2}
              variant="contained"
              sx={{
                height: '56px',
                padding: '6px 26px',
                '&.Mui-disabled': {
                  color: 'rgba(255, 255, 255, 0.3)',
                },
              }}
              onClick={() => {
                setCreators((prev) => {
                  if (textFieldValue?.current?.value) {
                    const value = textFieldValue.current.value
                    textFieldValue.current.value = ''
                    return [...prev, value]
                  } else {
                    return prev
                  }
                })
              }}
            >
              Add
            </Button>
          </div>
          <Typography
            sx={{
              fontSize: '22px',
              fontWeight: 700,
              color: 'white',
            }}
          >
            Accounts:
          </Typography>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            {creators.length ? (
              creators.map((creator) => {
                return (
                  <Chip
                    key={creator}
                    label={creator}
                    onDelete={() => {
                      setCreators((prev) => [
                        ...prev.filter((x) => x !== creator),
                      ])
                    }}
                    icon={
                      <IconStyle.InstagramIconStyle
                        style={{
                          color: Colors.pink,
                        }}
                      />
                    }
                    variant="outlined"
                    sx={{
                      borderColor: Colors.pink,
                      backgroundColor: 'rgba(226, 4, 120, 0.1)',
                      fontWeight: 700,
                      fontSize: '16px',
                      color: 'white',
                      padding: '10px 20px',
                      '& .MuiChip-deleteIcon': {
                        color: Colors.pink,
                        ':hover': {
                          color: '#b40360',
                        },
                      },
                    }}
                  />
                )
              })
            ) : (
              <p
                style={{
                  fontStyle: 'italic',
                  color: Colors.darkDeepGray,
                }}
              >
                Currently, no accounts have been added, please add the desired
                accounts.
              </p>
            )}
          </div>
          <Button
            disabled={creators.length !== 3}
            variant="contained"
            sx={{
              fontSize: '18px',
              padding: '6px 36px',
              marginTop: '20px',
              borderRadius: '30px',
              backgroundColor: Colors.pink,
              ':hover': {
                backgroundColor: '#b40360',
              },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                color: 'rgba(255, 255, 255, 0.3)',
              },
            }}
            onClick={() => {
              setOpen(false)
              refreshTargetCreators({
                variables: {
                  creators,
                },
              })
                .then(() => {
                  void refetch()
                })
                .catch((e) => {
                  console.log(e)
                  alert('Something went wrong.')
                })
            }}
          >
            Analyze
          </Button>
        </Box>
      </Modal>
    </div>
  )
}
